import { useEffect, useMemo, useRef, useState, useCallback, type Dispatch, type SetStateAction } from "react";
import { useLocation, Link } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import {
  AccountDraft,
  emptyDraft,
  setDraft,
  Stakeholder,
  StakeholderRole,
  PlanAction,
  MpactRow,
  Industry,
  Region,
  CEStatus,
  persistDraftFor,
  loadDraftFor,
  clearPersistedDraftFor,
  draftHasContent,
  draftStorageKey,
} from "@/lib/draftStore";
import { INDUSTRY_OPTIONS } from "@/lib/industries";
import { getHealthColorKey, type HealthColorKey } from "@shared/schema";
import { api, getSession, restoreSession } from "@/lib/session";
import { useTenantReps, type TenantRep } from "@/lib/useTenantReps";
import { useUnloadWarning } from "@/lib/useUnloadWarning";
import { toTitleCase } from "@/lib/text-utils";
import { Check, ChevronLeft, ChevronRight, Plus, Trash2, FileCheck, RotateCcw, X } from "lucide-react";

const STEPS = [
  { id: 1, label: "Account basics", desc: "Who and where" },
  { id: 2, label: "Firm / formal timeline", desc: "Why now" },
  { id: 3, label: "Stakeholder map", desc: "Who decides" },
  { id: 4, label: "M-PACT scoring", desc: "Calibrate the deal" },
  { id: 5, label: "Plan", desc: "30 / 60 / 90 day" },
  { id: 6, label: "Review & generate", desc: "Build the report" },
];

const INDUSTRIES: readonly Industry[] = INDUSTRY_OPTIONS;

const REGIONS: Region[] = [
  "US Midwest",
  "US Northeast",
  "US Southeast",
  "US West",
  "US Southwest",
  "Canada",
  "Mexico",
  "Other",
];
const ROLES: StakeholderRole[] = [
  "Decision-maker",
  "Influencer",
  "Champion",
  "Blocker",
  "Coach",
  "End-user",
];

// Health bands per CANON.md §5 — canonical 5-band palette via @shared/schema.
// `bandFor`/`bandColors` are kept as thin local adapters so the JSX call sites
// stay terse, but the threshold + color authority is the canonical helper.
function bandFor(score: number): HealthColorKey {
  return getHealthColorKey(score);
}

function bandColors(b: HealthColorKey): string {
  switch (b) {
    case "darkGreen":  return "text-emerald-200 bg-emerald-700/15 ring-emerald-700/40";
    case "lightGreen": return "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30";
    case "amber":      return "text-orange-300 bg-orange-500/10 ring-orange-500/30";
    case "orangeRed":  return "text-orange-300 bg-orange-500/10 ring-orange-500/30";
    case "darkRed":    return "text-rose-300 bg-rose-700/15 ring-rose-700/40";
  }
}

// Wizard identifier used in the localStorage key. Matches the naming in
// FIX-P0-2: `aip:wizard-draft:<user-id>:add-prospect`.
const WIZARD_NAME = "add-prospect";
// sessionStorage breadcrumb the plan report sets when the user clicks
// "Back to wizard" — lets us distinguish a return-from-report visit
// (restore the submitted snapshot) from a fresh visit after a successful
// save (start a brand-new empty wizard).
const RETURN_FLAG = "aip:wizard-return:add-prospect";

function getUserKey(): string {
  const s = getSession();
  return s?.email?.toLowerCase() || s?.id || "anonymous";
}

export default function NewAccount() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const userKey = useMemo(() => getUserKey(), []);
  const storageKey = useMemo(() => draftStorageKey(userKey, WIZARD_NAME), [userKey]);

  // ── Initial state: decide whether to restore a saved draft ──────────────
  // Computed synchronously so the first render already shows the restored
  // data when appropriate (no flicker, no race with autosave).
  const initial = useMemo(() => {
    const saved = loadDraftFor(userKey, WIZARD_NAME);
    const returning =
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem(RETURN_FLAG) === "1";
    if (returning && saved) {
      // Came back from plan report — restore silently, clear the flag.
      try { window.sessionStorage.removeItem(RETURN_FLAG); } catch {}
      return { data: saved, showBanner: false };
    }
    if (saved && saved.submittedAt && !returning) {
      // Previous wizard was already submitted and the user is opening a
      // fresh /add-prospect (not coming back from the report) — start
      // clean and wipe the stale completed snapshot.
      clearPersistedDraftFor(userKey, WIZARD_NAME);
      return { data: emptyDraft(), showBanner: false };
    }
    if (saved && draftHasContent(saved)) {
      // In-progress draft from a prior session/tab — offer to resume.
      return { data: saved, showBanner: true };
    }
    return { data: emptyDraft(), showBanner: false };
  }, [userKey]);

  const [step, setStep] = useState(1);
  const [data, setData] = useState<AccountDraft>(() => initial.data);
  const [showRestoreBanner, setShowRestoreBanner] = useState(initial.showBanner);
  const { reps, loading: repsLoading } = useTenantReps();
  const [submitting, setSubmitting] = useState(false);
  // Tracks whether the in-memory draft differs from what's been persisted
  // — used to gate the beforeunload prompt.
  const dirtyRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always holds the latest data so the unmount flush can read it synchronously
  // without a stale closure.
  const dataRef = useRef<AccountDraft>(initial.data);

  // Keep dataRef in sync with data on every render.
  dataRef.current = data;

  // Synchronous flush — called on unmount (hash back-nav) and page events.
  const flushDraft = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    persistDraftFor(userKey, WIZARD_NAME, dataRef.current);
    dirtyRef.current = false;
  }, [userKey]);

  // ── Debounced autosave to localStorage on every change (800 ms) ──────────
  useEffect(() => {
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persistDraftFor(userKey, WIZARD_NAME, data);
      dirtyRef.current = false;
    }, 800);
    // NOTE: cleanup cancels the timer but does NOT flush here — the dedicated
    // unmount effect below handles that synchronously via dataRef.
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [data, userKey]);

  // ── Flush on component unmount (hash-navigation back/forward) ────────────
  // This is the PRIMARY FIX: in a hash-routed SPA, browser back changes the
  // hash without firing pagehide/visibilitychange, so the only reliable flush
  // point is the React component unmount cleanup.
  useEffect(() => {
    return () => {
      // Only flush if there is meaningful content — avoid writing empty drafts
      // that would then prompt "resume draft" on the next fresh visit.
      if (draftHasContent(dataRef.current)) {
        flushDraft();
      }
    };
  }, [flushDraft]);

  // Flush pending writes when the tab is hidden / unloaded so we never
  // lose the last 800 ms of edits.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushDraft();
    };
    window.addEventListener("pagehide", flushDraft);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushDraft);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [flushDraft]);

  // beforeunload + in-app nav guard — warn before browser back / refresh /
  // close / Link click while there is meaningful unsaved content. localStorage
  // already preserves it, but a confirm prompt prevents accidental nav.
  useUnloadWarning(
    draftHasContent(data) && !data.submittedAt,
    "You have unsaved account data that has not been sent to the server yet."
  );

  // Cross-tab sync: when another tab writes to the same draft key, mirror
  // the change so both tabs stay consistent.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey || !e.newValue) return;
      try {
        const next = JSON.parse(e.newValue) as AccountDraft;
        if (next && typeof next === "object") {
          setData(next);
        }
      } catch {
        /* ignore malformed payloads */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  const update = <K extends keyof AccountDraft>(k: K, v: AccountDraft[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  const startFresh = () => {
    clearPersistedDraftFor(userKey, WIZARD_NAME);
    const fresh = emptyDraft();
    setData(fresh);
    dataRef.current = fresh;
    setStep(1);
    setShowRestoreBanner(false);
  };
  const dismissBanner = () => setShowRestoreBanner(false);

  const composite = useMemo(() => {
    const sum = data.mpact.reduce((acc, r) => acc + r.score, 0);
    return Math.round(sum / data.mpact.length);
  }, [data.mpact]);

  const goNext = () => setStep((s) => Math.min(STEPS.length, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  // D-030 (2026-05-20): Step 1 requires Industry, Region, ARR, and Owner.
  // Continue button stays disabled until all four are set so the wizard can no
  // longer advance with blanks (the previous behavior silently created
  // accounts that never appeared in the pipeline list).
  const step1Valid =
    !!data.industry &&
    !!data.region &&
    typeof data.arrPotential === "number" &&
    data.arrPotential > 0 &&
    !!data.repId;

  const persistAccount = async (): Promise<{ id: number } | null> => {
    const name = data.company?.trim();
    if (!name) {
      toast({
        title: "Save failed \u2014 try again",
        description: "Account name is required.",
        variant: "destructive" as any,
      });
      return null;
    }
    setSubmitting(true);
    try {
      // COLD-START RACE FIX: ensure session is fully hydrated before firing
      // the POST. On a fresh browser session the _cached session may not yet
      // have a resolved tenantId. restoreSession() re-queries the users table
      // and populates the cache with the real tenant_id row value.
      let sess = getSession();
      if (!sess || !sess.tenantId || sess.tenantId <= 0) {
        sess = await restoreSession();
      }
      if (!sess || !sess.tenantId || sess.tenantId <= 0) {
        throw new Error("Your session has not fully loaded yet. Please wait a moment and try again.");
      }

      // P0-SCHEMA-STRIP FIX: ship the full wizard draft. The server now
      // accepts aip_ext (jsonb) plus optional cp_answers / cp_statuses
      // arrays and merges them into accounts.aip_ext / account_answers /
      // account_cp_status. Previously only {name, industry, stage, estArr}
      // were sent and the rest was silently dropped by zod.
      const aipExt: Record<string, any> = {
        bant: {
          // The wizard does not capture BANT directly today, but the report
          // step writes BANT into the AccountDraft when present. Forward
          // whatever is on the draft so child surfaces (M-PACT, stakeholders,
          // 30-60-90) make it to the server.
        },
        mpact: data.mpact,
        stakeholders: data.stakeholders,
        risks: data.risks,
        plan30_60_90: {
          day30: data.next30,
          day60: data.next60,
          day90: data.next90,
        },
        approach: data.approach,
        ce_status: data.ceStatus,
        ce_description: data.ceDescription,
        ce_decision_date: data.ceDecisionDate,
        ce_confidence: data.ceConfidence,
        region: data.region,
        employees: typeof data.employees === "number" ? data.employees : null,
        est_arr: typeof data.arrPotential === "number" ? data.arrPotential : 0,
      };

      const body = {
        name,
        industry: data.industry || "",
        stage: "Suspect",
        estArr: typeof data.arrPotential === "number" ? data.arrPotential : 0,
        aip_ext: aipExt,
      };

      const doPost = () => api<{
        id: number;
        persisted?: { aip_ext: boolean; cp_answers: number; cp_statuses: number };
        persistErrors?: string[];
      }>("/api/accounts", {
        method: "POST",
        body: JSON.stringify(body),
      });

      let created: {
        id: number;
        persisted?: { aip_ext: boolean; cp_answers: number; cp_statuses: number };
        persistErrors?: string[];
      };
      try {
        created = await doPost();
      } catch (firstErr: any) {
        // COLD-START RACE FIX: if the server returns TENANT_NOT_RESOLVED it
        // means the function cold-started and the user row wasn't yet in cache.
        // Re-hydrate the session and retry up to two times with a short delay.
        if (firstErr?.body?.code === "TENANT_NOT_RESOLVED" || firstErr?.status === 401) {
          await new Promise((r) => setTimeout(r, 800));
          await restoreSession();
          try {
            created = await doPost();
          } catch (secondErr: any) {
            if (secondErr?.body?.code === "TENANT_NOT_RESOLVED" || secondErr?.status === 401) {
              await new Promise((r) => setTimeout(r, 1500));
              await restoreSession();
              created = await doPost();
            } else {
              throw secondErr;
            }
          }
        } else {
          throw firstErr;
        }
      }

      if (!created || typeof created.id !== "number") {
        throw new Error("Server did not return an account id.");
      }

      // Verify the server actually persisted the fat payload before we treat
      // this as a clean save. If aip_ext didn't get written or the server
      // returned persist errors, keep the localStorage draft and warn the user.
      const echoedAipExt = created.persisted?.aip_ext === true;
      const persistErrors = created.persistErrors ?? [];
      const wantedAipExt = !!body.aip_ext && Object.keys(body.aip_ext).length > 0;
      if ((wantedAipExt && !echoedAipExt) || persistErrors.length > 0) {
        console.warn("[P0-SCHEMA-STRIP] server did not echo persisted fields", created);
        toast({
          title: "Saved partially",
          description:
            "Account created, but some wizard fields did not save. Click Save Draft again or contact support before closing this tab.",
          variant: "destructive" as any,
        });
        // Return the id so navigation continues; caller decides whether to
        // clear localStorage. Note: persistDraftFor at the call site keeps
        // the local copy as a safety net regardless.
      }

      return created;
    } catch (e: any) {
      toast({
        title: "Save failed \u2014 try again",
        description: e?.message ?? "Could not save account to server.",
        variant: "destructive" as any,
      });
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    // Mark the draft as submitted before persisting + handing off to the
    // plan report. The plan-report "Back to wizard" link sets the
    // sessionStorage RETURN_FLAG so the next NewAccount mount restores this
    // snapshot. Any other fresh /add-prospect visit will see submittedAt
    // and wipe the snapshot so the new wizard starts empty.
    const submitted: AccountDraft = {
      ...data,
      submittedAt: new Date().toISOString(),
    };
    setDraft(submitted);
    persistDraftFor(userKey, WIZARD_NAME, submitted);
    setData(submitted);
    dirtyRef.current = false;

    const created = await persistAccount();
    if (!created) return;
    toast({
      title: "Account saved",
      description: `${data.company.trim()} is now in your accounts list.`,
    });
    // D-030 (2026-05-20): land on the new account's detail page instead of
    // the filtered pipeline list, so the user immediately sees what they
    // just created. Previously the redirect went to /plan-report which the
    // user can navigate to from the detail page anyway.
    navigate(`/account/${created.id}`);
  };

  const handleSaveDraft = async () => {
    const submitted: AccountDraft = {
      ...data,
      submittedAt: new Date().toISOString(),
    };
    setDraft(submitted);
    persistDraftFor(userKey, WIZARD_NAME, submitted);
    setData(submitted);
    dirtyRef.current = false;

    const created = await persistAccount();
    if (!created) return;
    toast({
      title: "Account saved",
      description: "Draft persisted. You can return any time.",
    });
    navigate(`/plan-report/${created.id}`);
  };

  return (
    <OperatorShell breadcrumbs={[{ label: "Home", href: "/" }, { label: "Accounts", href: "/accounts" }, { label: "New Account" }]}>
      {showRestoreBanner && (
        <div
          role="status"
          className="mb-6 flex items-start gap-3 rounded border border-orange-500/30 bg-orange-500/10 px-4 py-3"
          data-testid="draft-restored-banner"
        >
          <RotateCcw className="w-4 h-4 mt-0.5 text-orange-300 shrink-0" />
          <div className="flex-1 text-sm text-orange-100">
            <div className="font-medium">
              Resume draft from{" "}
              {data.createdAt
                ? (() => {
                    const diffMs = Date.now() - new Date(data.createdAt).getTime();
                    const diffMin = Math.floor(diffMs / 60000);
                    const diffHr = Math.floor(diffMin / 60);
                    if (diffMin < 1) return "just now";
                    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
                    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
                    return `${Math.floor(diffHr / 24)} day${Math.floor(diffHr / 24) === 1 ? "" : "s"} ago`;
                  })()
                : "earlier"}?
            </div>
            <div className="text-orange-200/80 text-[13px] mt-0.5">
              {data.company ? `"${data.company}" — ` : ""}
              All your entries from this device were saved automatically. Resume
              where you left off, or discard and start fresh.
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={dismissBanner}
            className="h-9 md:h-8 text-xs"
            data-testid="draft-resume-btn"
          >
            Resume
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={startFresh}
            className="h-9 md:h-8 text-xs"
            data-testid="draft-discard-btn"
          >
            Discard
          </Button>
          <button
            onClick={dismissBanner}
            aria-label="Dismiss"
            className="text-orange-300/70 hover:text-orange-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
          New account wizard
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">Add a new account</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Six steps. Capture the account, the event, the people, and the plan. The
          system generates a sales plan report that you can present, print, or hand
          to your manager. Demo tip — to skip the wizard, view a populated sample at{" "}
          <Link href="/plan-report/sample" className="underline hover:text-foreground">
            /plan-report/sample
          </Link>
          .
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Stepper */}
        <aside className="lg:sticky lg:top-6 self-start">
          <ol className="space-y-1">
            {STEPS.map((s) => {
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => setStep(s.id)}
                    className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded border transition-colors ${
                      isActive
                        ? "border-foreground/40 bg-[hsl(var(--surface-2))]"
                        : "border-transparent hover:bg-[hsl(var(--surface-2))]"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex items-center justify-center w-6 h-6 rounded-full text-[11px] tabular ring-1 ${
                        isDone
                          ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
                          : isActive
                          ? "bg-foreground text-background ring-foreground/40"
                          : "bg-transparent text-muted-foreground ring-border"
                      }`}
                    >
                      {isDone ? <Check className="w-3 h-3" /> : s.id}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-medium tracking-tight">
                        {s.label}
                      </span>
                      <span className="block text-[11px] text-muted-foreground mt-0.5">
                        {s.desc}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="mt-6 px-3 text-[11px] text-muted-foreground tabular">
            Step {step} of {STEPS.length}
          </div>
        </aside>

        {/* Form panel */}
        <section data-tour="new-opportunity-form" className="card-shell p-6 sm:p-8 min-h-[520px] flex flex-col">
          <div className="flex items-baseline justify-between mb-6 pb-4 border-b border-border">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                Step {step}
              </div>
              <h2 className="text-xl font-semibold tracking-tight mt-0.5">
                {STEPS[step - 1].label}
              </h2>
            </div>
            {step === 4 && (
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  Live composite
                </span>
                <span
                  className={`tabular text-2xl font-semibold px-3 py-1 rounded ring-1 ring-inset ${bandColors(
                    bandFor(composite),
                  )}`}
                >
                  {composite}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1">
            {step === 1 && (
              <Step1
                data={data}
                update={update}
                setData={setData}
                reps={reps}
                repsLoading={repsLoading}
              />
            )}
            {step === 2 && <Step2 data={data} update={update} />}
            {step === 3 && <Step3 data={data} update={update} />}
            {step === 4 && <Step4 data={data} update={update} />}
            {step === 5 && <Step5 data={data} update={update} />}
            {step === 6 && (
              <Step6
                data={data}
                onGenerate={handleGenerate}
                onSaveDraft={handleSaveDraft}
              />
            )}
          </div>

          {/* D-030 step-1 required-field hint */}
          {step === 1 && !step1Valid && (
            <div className="mt-6 text-[11px] text-muted-foreground">
              Industry, Region, ARR potential, and Owner are required to continue.
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={step === 1}
              className="gap-1.5"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {step < STEPS.length && (
                <Button
                  onClick={goNext}
                  disabled={step === 1 && !step1Valid}
                  // D-039 (2026-05-20): the baseline Button only applies
                  // opacity-50 on disabled, which against the dark primary
                  // background still reads as enabled. These extra classes
                  // force a clearly greyed-out look (muted bg, muted text,
                  // not-allowed cursor) so users see the gate immediately.
                  className="gap-1.5 disabled:bg-muted disabled:text-muted-foreground disabled:border-muted disabled:cursor-not-allowed disabled:hover:bg-muted disabled:opacity-100"
                  data-testid="wizard-continue-btn"
                  title={step === 1 && !step1Valid ? "Industry, Region, ARR potential, and Owner are required." : undefined}
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              {step === STEPS.length && (
                <Button onClick={handleGenerate} disabled={submitting} className="gap-1.5">
                  <FileCheck className="w-4 h-4" /> {submitting ? "Saving..." : "Generate Sales Plan Report"}
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
    </OperatorShell>
  );
}

/* ---------------- Step components ---------------- */

type UpdateFn = <K extends keyof AccountDraft>(k: K, v: AccountDraft[K]) => void;

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </Label>
  );
}

function Step1({
  data,
  update,
  setData,
  reps,
  repsLoading,
}: {
  data: AccountDraft;
  update: UpdateFn;
  setData: Dispatch<SetStateAction<AccountDraft>>;
  reps: TenantRep[];
  repsLoading: boolean;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="md:col-span-2 space-y-1.5">
        <FieldLabel>Company name</FieldLabel>
        <Input
          value={data.company}
          onChange={(e) => update("company", e.target.value)}
          placeholder="Account name"
        />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Industry</FieldLabel>
        <Select
          value={data.industry || undefined}
          onValueChange={(v) => update("industry", v as Industry)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent>
            {INDUSTRIES.map((i) => (
              <SelectItem key={i} value={i}>
                {i}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Region</FieldLabel>
        <Select
          value={data.region || undefined}
          onValueChange={(v) => update("region", v as Region)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select region" />
          </SelectTrigger>
          <SelectContent>
            {REGIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Estimated employees</FieldLabel>
        <Input
          type="number"
          value={data.employees === "" ? "" : data.employees}
          onChange={(e) =>
            update("employees", e.target.value === "" ? "" : Number(e.target.value))
          }
          placeholder="e.g., 1250"
        />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Estimated ARR potential (USD)</FieldLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            $
          </span>
          <Input
            type="number"
            value={data.arrPotential === "" ? "" : data.arrPotential}
            onChange={(e) =>
              update(
                "arrPotential",
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            placeholder="e.g., 480000"
            className="pl-6"
          />
        </div>
        {typeof data.arrPotential === "number" && data.arrPotential > 0 && (
          <div className="text-[11px] text-muted-foreground tabular">
            ~ ${(data.arrPotential / 1000).toFixed(0)}k
          </div>
        )}
      </div>
      <div className="space-y-1.5 md:col-span-2">
        <FieldLabel>Owner</FieldLabel>
        <Select
          value={data.repId || undefined}
          onValueChange={(repId) => {
            const rep = reps.find((r) => r.id === repId);
            setData((d) => ({
              ...d,
              repId,
              rep: rep?.name ?? "",
            }));
          }}
          disabled={repsLoading || reps.length === 0}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                repsLoading
                  ? "Loading reps..."
                  : reps.length === 0
                  ? "No AEs found for this tenant"
                  : "Select AE"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {reps.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function Step2({ data, update }: { data: AccountDraft; update: UpdateFn }) {
  return (
    <div className="space-y-6">
      <div className="rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-neutral-700">
        Most deals do not have a formal timeline — and that is fine. What we look for is a firm or formal date the buyer has set for this project. Capture what is real. A true formal timeline (an external dated event with significant consequence) is the rare upper tier and cannot be manufactured.
      </div>

      <div className="space-y-2">
        <FieldLabel>Has a firm or formal timeline been identified</FieldLabel>
        <RadioGroup
          value={data.ceStatus ?? ""}
          onValueChange={(v) => update("ceStatus", v as CEStatus)}
          className="grid grid-cols-3 gap-3 mt-2"
        >
          {(["yes", "suspected", "no"] as CEStatus[]).map((v) => (
            <label
              key={v}
              className={`flex items-center gap-3 px-4 py-3 rounded border cursor-pointer transition-colors ${
                data.ceStatus === v
                  ? "border-foreground/40 bg-[hsl(var(--surface-2))]"
                  : "border-border hover:bg-[hsl(var(--surface-2))]"
              }`}
            >
              <RadioGroupItem value={v} />
              <span className="capitalize text-sm">{v}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      {data.ceStatus !== undefined && data.ceStatus !== "no" && (
        <div className="space-y-1.5">
          <FieldLabel>Event description</FieldLabel>
          <Textarea
            rows={4}
            value={data.ceDescription}
            onChange={(e) => update("ceDescription", e.target.value)}
            placeholder="Describe the trigger. RFP issued, regulatory deadline, leadership change, contract expiry, board mandate..."
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <FieldLabel>Decision date</FieldLabel>
          <Input
            type="date"
            value={data.ceDecisionDate}
            onChange={(e) => update("ceDecisionDate", e.target.value)}
          />
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <FieldLabel>Confidence</FieldLabel>
            {data.ceConfidence !== undefined && (
              <span className="tabular text-sm">{data.ceConfidence}%</span>
            )}
          </div>
          {data.ceConfidence === undefined ? (
            <div className="text-sm text-neutral-400 italic py-1">Set confidence</div>
          ) : (
            <Slider
              value={[data.ceConfidence]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => update("ceConfidence", v[0])}
            />
          )}
          {data.ceConfidence === undefined && (
            <Slider
              value={[0]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => update("ceConfidence", v[0])}
              className="opacity-40"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Step3({ data, update }: { data: AccountDraft; update: UpdateFn }) {
  const updateRow = (i: number, patch: Partial<Stakeholder>) => {
    const next = data.stakeholders.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    update("stakeholders", next);
  };
  const addRow = () =>
    update("stakeholders", [
      ...data.stakeholders,
      { name: "", title: "", role: "", engagement: 3 },
    ]);
  const removeRow = (i: number) =>
    update(
      "stakeholders",
      data.stakeholders.filter((_, idx) => idx !== i),
    );

  return (
    <div className="space-y-5">
      <div className="text-sm text-muted-foreground italic">
        Map the people. Map the politics.
      </div>

      <div className="card-shell-2 overflow-hidden">
        <div className="grid grid-cols-[1.4fr_1.2fr_1.1fr_140px_40px] gap-3 px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Name</span>
          <span>Title</span>
          <span>Role</span>
          <span>Engagement</span>
          <span></span>
        </div>
        <div>
          {data.stakeholders.map((s, i) => (
            <div
              key={i}
              className="grid grid-cols-[1.4fr_1.2fr_1.1fr_140px_40px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center"
            >
              <Input
                value={s.name}
                onChange={(e) => updateRow(i, { name: e.target.value })}
                placeholder="Name"
              />
              <Input
                value={s.title}
                onChange={(e) => updateRow(i, { title: e.target.value })}
                placeholder="Title"
              />
              <Select
                value={s.role || undefined}
                onValueChange={(v) => updateRow(i, { role: v as StakeholderRole })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Slider
                  value={[s.engagement]}
                  min={1}
                  max={5}
                  step={1}
                  onValueChange={(v) => updateRow(i, { engagement: v[0] })}
                />
                <span className="tabular text-xs w-4">{s.engagement}</span>
              </div>
              <button
                onClick={() => removeRow(i)}
                disabled={data.stakeholders.length <= 1}
                className="text-muted-foreground hover:text-rose-400 disabled:opacity-30"
                aria-label="Remove stakeholder"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Button variant="outline" onClick={addRow} className="gap-1.5">
        <Plus className="w-4 h-4" /> Add stakeholder
      </Button>
    </div>
  );
}

function Step4({ data, update }: { data: AccountDraft; update: UpdateFn }) {
  const updateRow = (i: number, patch: Partial<MpactRow>) => {
    const next = data.mpact.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    update("mpact", next);
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        Score each dimension on evidence, not opinion. Composite shown above is the
        average across the five pillars.
      </div>
      <div className="card-shell-2 divide-y divide-border">
        {data.mpact.map((r, i) => {
          const band = bandFor(r.score);
          return (
            <div
              key={r.key}
              className="px-4 py-4"
            >
              {/* Mobile: stacked layout; Desktop: single row */}
              <div className="flex flex-col md:grid md:grid-cols-[60px_1fr_240px_48px] md:gap-4 md:items-center gap-2">
                {/* Row 1 on mobile: letter badge + label */}
                <div className="flex items-center gap-3 md:block md:gap-0">
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded ring-1 ring-inset font-semibold tabular flex-shrink-0 ${bandColors(
                      band,
                    )}`}
                  >
                    {r.letter}
                  </span>
                  <div className="text-[13px] font-medium tracking-tight md:hidden">{r.label}</div>
                </div>
                {/* Label + Evidence — desktop shows label inside this col, mobile label is above */}
                <div className="space-y-1">
                  <div className="text-[13px] font-medium tracking-tight hidden md:block">{r.label}</div>
                  <Input
                    value={r.evidence}
                    onChange={(e) => updateRow(i, { evidence: e.target.value })}
                    placeholder="Evidence — one line"
                    className="h-8 text-[13px] w-full"
                  />
                </div>
                {/* Slider — full width on mobile */}
                <Slider
                  value={[r.score]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={(v) => updateRow(i, { score: v[0] })}
                />
                {/* Score number */}
                <span className="tabular text-right text-sm w-12">{r.score}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step5({ data, update }: { data: AccountDraft; update: UpdateFn }) {
  const updateRisk = (i: number, v: string) => {
    const next = [...data.risks];
    next[i] = v;
    update("risks", next);
  };
  const updateAction = (
    list: "next30" | "next60" | "next90",
    i: number,
    patch: Partial<PlanAction>,
  ) => {
    const next = data[list].map((a, idx) => (idx === i ? { ...a, ...patch } : a));
    update(list, next);
  };

  return (
    <div className="space-y-7">
      <div className="space-y-1.5">
        <FieldLabel>Sales approach</FieldLabel>
        <Textarea
          rows={3}
          value={data.approach}
          onChange={(e) => update("approach", e.target.value)}
          placeholder="How will you penetrate this account? Who do you lead with, who do you isolate, what is the narrative?"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Top three risks</FieldLabel>
        <div className="space-y-2">
          {data.risks.map((r, i) => (
            <Input
              key={i}
              value={r}
              onChange={(e) => updateRisk(i, e.target.value)}
              placeholder={`Risk ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {(["next30", "next60", "next90"] as const).map((list, idx) => (
        <PlanList
          key={list}
          title={`Next ${[30, 60, 90][idx]} days`}
          rows={data[list]}
          onChange={(i, patch) => updateAction(list, i, patch)}
        />
      ))}
    </div>
  );
}

function PlanList({
  title,
  rows,
  onChange,
}: {
  title: string;
  rows: PlanAction[];
  onChange: (i: number, patch: Partial<PlanAction>) => void;
}) {
  return (
    <div className="space-y-2">
      <FieldLabel>{title}</FieldLabel>
      <div className="card-shell-2 overflow-hidden">
        <div className="grid grid-cols-[1.5fr_0.8fr_0.6fr] gap-3 px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Action</span>
          <span>Owner</span>
          <span>Due date</span>
        </div>
        {rows.map((r, i) => (
          <div
            key={i}
            className="grid grid-cols-[1.5fr_0.8fr_0.6fr] gap-3 px-3 py-2 border-b border-border last:border-b-0"
          >
            <Input
              value={r.action}
              onChange={(e) => onChange(i, { action: e.target.value })}
              placeholder="Action"
              className="h-8 text-[13px]"
            />
            <Input
              value={r.owner}
              onChange={(e) => onChange(i, { owner: e.target.value })}
              placeholder="Owner"
              className="h-8 text-[13px]"
            />
            <Input
              value={r.due}
              onChange={(e) => onChange(i, { due: e.target.value })}
              placeholder="Due"
              className="h-8 text-[13px]"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Step6({
  data,
  onGenerate,
  onSaveDraft,
}: {
  data: AccountDraft;
  onGenerate: () => void | Promise<void>;
  onSaveDraft: () => void;
}) {
  const arrFmt =
    typeof data.arrPotential === "number" && data.arrPotential > 0
      ? `$${(data.arrPotential / 1000).toFixed(0)}k`
      : "—";
  const composite = Math.round(
    data.mpact.reduce((s, r) => s + r.score, 0) / data.mpact.length,
  );
  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        Final review. Confirm everything looks right, then generate the report.
      </div>

      <ReviewSection title="Account basics">
        <ReviewKV k="Company" v={data.company ? toTitleCase(data.company) : <span className="text-neutral-400 italic">Not entered</span>} />
        <ReviewKV k="Industry" v={data.industry || <span className="text-neutral-400 italic">Not entered</span>} />
        <ReviewKV k="Region" v={data.region || <span className="text-neutral-400 italic">Not entered</span>} />
        <ReviewKV
          k="Employees"
          v={typeof data.employees === "number" ? data.employees.toLocaleString() : <span className="text-neutral-400 italic">Not entered</span>}
        />
        <ReviewKV k="ARR potential" v={arrFmt !== "—" ? arrFmt : <span className="text-neutral-400 italic">Not entered</span>} />
        <ReviewKV k="Owner" v={data.rep || <span className="text-neutral-400 italic">Not entered</span>} />
      </ReviewSection>

      <ReviewSection title="Firm / formal timeline">
        <ReviewKV k="Status" v={data.ceStatus ?? <span className="text-neutral-400 italic">Not entered</span>} />
        <ReviewKV k="Decision date" v={data.ceDecisionDate || <span className="text-neutral-400 italic">Not entered</span>} />
        {data.ceConfidence !== undefined && (
          <ReviewKV k="Confidence" v={`${data.ceConfidence}%`} />
        )}
        <div className="md:col-span-3 text-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
            Description
          </div>
          <div>{data.ceDescription || <span className="text-neutral-400 italic">Not entered</span>}</div>
        </div>
      </ReviewSection>

      <ReviewSection title="Stakeholders">
        <div className="md:col-span-3 space-y-1.5">
          {data.stakeholders.filter((s) => s.name).length === 0 && (
            <div className="text-sm text-muted-foreground">No stakeholders entered.</div>
          )}
          {data.stakeholders
            .filter((s) => s.name)
            .map((s, i) => (
              <div key={i} className="text-sm flex flex-wrap gap-x-3">
                <span className="font-medium">{s.name}</span>
                <span className="text-muted-foreground">{s.title}</span>
                <span className="text-muted-foreground">· {s.role || "—"}</span>
                <span className="text-muted-foreground">· engagement {s.engagement}/5</span>
              </div>
            ))}
        </div>
      </ReviewSection>

      <ReviewSection title={`Composite ${composite}`}>
        <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          {data.mpact.map((r) => (
            <div key={r.key} className="text-sm">
              <span className="font-medium">{r.letter} {r.label}:</span>{" "}
              <span className="tabular">{r.score}</span>
              {r.evidence && (
                <div className="text-xs text-muted-foreground italic mt-0.5">
                  {r.evidence}
                </div>
              )}
            </div>
          ))}
        </div>
      </ReviewSection>

      <ReviewSection title="Plan">
        <div className="md:col-span-3 space-y-3 text-sm">
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">
              Approach
            </span>
            {data.approach || "—"}
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">
              Risks
            </span>
            <ol className="list-decimal pl-5 space-y-0.5">
              {data.risks.filter(Boolean).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
              {data.risks.filter(Boolean).length === 0 && <li>—</li>}
            </ol>
          </div>
        </div>
      </ReviewSection>

      <div className="flex flex-wrap gap-3 pt-2">
        <Button onClick={onGenerate} className="gap-1.5">
          <FileCheck className="w-4 h-4" /> Generate Sales Plan Report
        </Button>
        <Button variant="outline" onClick={onSaveDraft}>
          Save Draft
        </Button>
      </div>
    </div>
  );
}

function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card-shell-2 p-5">
      <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
        {title}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

function ReviewKV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">
        {k}
      </div>
      <div>{v}</div>
    </div>
  );
}
